import { Link, useSearchParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';

import { getProviderClass, getProviderDisplayName } from './utils/providers.js';
import './Movie.css';


const Movie = ({movie}) => {
    const [trailer, setTrailer] = useState(null);
    const [trailer_id, setTrailerId] = useState(null);
    const [showProviders, setShowProviders] = useState(false);
    const [loadingTrailer, setLoadingTrailer] = useState(false);
    const [trailerError, setTrailerError] = useState(null);
    const [expandedDescriptions, setExpandedDescriptions] = useState({});
    const [showTrailerModal, setShowTrailerModal] = useState(false);

    const [searchParams] = useSearchParams();
    const tags = searchParams.get('tags');

    const query = tags ? `?tags=${tags}` : '';

    let url = process.env.REACT_APP_API_URL;
    if (url === undefined) {
        url = "http://localhost:5051";
    }

    const setLoadingText = (loadingTrailer, movie_id, trailer, trailer_id) => {
        if (trailerError) {
             return trailerError;
        }
        if (loadingTrailer && movie_id === trailer_id) {
          return 'Loading Trailer...';
        }
        else {
        	return 'Trailer';
        }
    }

    const loadTrailer = async (movie) => {
      // If trailer is already loaded for this movie, show modal
      if (trailer && trailer_id === movie.id) {
        setShowTrailerModal(true);
        return;
      }

      // If trailer is loaded for different movie, reset and load new one
      if (trailer) {
        setTrailer(null);
        setTrailerId(null);
      }

      setTrailerId(movie.id);
      setLoadingTrailer(true);
      setTrailerError(null);

      let code = null;
      for (const link of movie.links) {
        if (link.label === "Trailer") {
            for (const chunk of link.url.split("/")) {
                code = chunk;
            };
        }
      }

      if (code) {
          setTrailer({"code": code, "id": movie.id});
          setShowTrailerModal(true);
      }
      else {
         setTrailerError("No trailer available!");
      }

      setLoadingTrailer(false);
    };

    const closeTrailerModal = () => {
        setShowTrailerModal(false);
        // Don't clear trailer data so we can reopen quickly
    };

    const toggleProviders = (movieId) => {
      setShowProviders(prevState => ({
        ...prevState,
        [movieId]: !prevState[movieId]
      }));
    };

    const toggleDescription = (movieId) => {
      setExpandedDescriptions(prev => ({
        ...prev,
        [movieId]: !prev[movieId]
      }));
    };

    let cover = null;
    for (const link of movie.links) {
        if (link.label === "Cover") {
            cover = link.url;
        }
    }

	return (
		<>
			{/* Trailer Modal */}
			{showTrailerModal && trailer && (
				<TrailerModal 
					trailer={trailer}
					onClose={closeTrailerModal}
					movieTitle={movie.title}
				/>
			)}

			<div key={movie.id} className="movie-item">
				{/* Movie Media - Cover Image */}
				<div className="movie-media">
					<Link to={`/movie/${movie.slug}${query}`} className="movie-cover-link">
						<img src={cover} alt={movie.title} className="movie-cover" />
						<div className="cover-overlay">
							<span className="view-details">View Details</span>
						</div>
					</Link>
				</div>

				{/* Movie Basic Info */}
				<div className="movie-basic-info">
					<h3 className="movie-card-title">{movie.title}</h3>
					<div className="movie-year">{movie.release_year}</div>
				</div>

				{/* Movie Details */}
				<div className="movie-details">
					{/* Rating Section */}
					<div className="rating-section">
						<div className="star-rating">
							{[...Array(5)].map((_, i) => (
								<span 
									key={i} 
									className={`star ${i < Math.round(movie.rating / 200 + 0.25) ? 'star-filled' : 'star-empty'}`}
								>
									{i < Math.round(movie.rating / 200 + 0.25) ? '★' : '☆'}
								</span>
							))}
						</div>
						<div className="rating-pill">{movie.rating / 100}/10</div>
					</div>

					{/* Description */}
					<div className="movie-description-section">
						<div className="movie-description">
							{expandedDescriptions[movie.id] ? (
								<div className="description-full">{movie.description}</div>
							) : (
								<div className="description-truncated">
									{movie.description.substring(0, 100)}
									{movie.description.length > 100 && '...'}
								</div>
							)}
							{movie.description.length > 100 && (
								<button 
									className="read-more-btn"
									onClick={() => toggleDescription(movie.id)}
									aria-expanded={expandedDescriptions[movie.id] || false}
								>
									{expandedDescriptions[movie.id] ? 'Show less' : 'Read more'}
								</button>
							)}
						</div>
					</div>

					{/* Action Buttons */}
					<div className="movie-actions">
						<button 
							onClick={() => loadTrailer(movie)} 
							disabled={loadingTrailer}
							className="action-button trailer-button"
						>
							{setLoadingText(loadingTrailer, trailer_id, trailer, movie.id)}
						</button>
						
						<button 
							onClick={() => toggleProviders(movie.id)}
							className="action-button providers-button"
							aria-expanded={showProviders[movie.id] || false}
						>
							Links
						</button>
					</div>

					{/* Provider Links */}
					{showProviders[movie.id] && (
						<div className="provider-links">
							<h4 className="provider-title">Where to Watch:</h4>
							<div className="provider-list">
								{movie.links.filter(link => link.label === "Provider").map((link) => {
                                    const url = new URL(link.url);
                                    const hostname = url.hostname.replace('www.', '');
                                    const providerClass = getProviderClass(hostname);
                                    const displayName = getProviderDisplayName(hostname);

                                    return (
                                      <a 
                                        key={link.url}
                                        href={link.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className={`provider-link ${providerClass}`}
                                      >
                                        <span className="provider-name">{displayName}</span>
                                        <span className="external-icon">↗</span>
                                      </a>
                                    );
								})}
							</div>
						</div>
					)}
				</div>
			</div>
		</>
	);
};

// TrailerModal Component
const TrailerModal = ({ trailer, onClose, movieTitle }) => {
    useEffect(() => {
        document.body.classList.add('trailer-open');
        
        // Add escape key listener
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        
        document.addEventListener('keydown', handleEscape);
        
        return () => {
            document.body.classList.remove('trailer-open');
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    return (
        <div className="trailer-modal-overlay" onClick={onClose}>
            <div className="trailer-modal-content" onClick={(e) => e.stopPropagation()}>
                <button 
                    className="close-trailer-btn"
                    onClick={onClose}
                    title="Close trailer"
                >
                    ×
                </button>
                <div className="trailer-iframe-container">
                    <iframe 
                        src={`https://www.youtube.com/embed/${trailer.code}?autoplay=1`}
                        title={`${movieTitle} Trailer`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        className="trailer-embed"
                    ></iframe>
                </div>
                <div className="trailer-modal-info">
                    <h3>{movieTitle}</h3>
                    <p>Click the fullscreen button for the best viewing experience</p>
                </div>
            </div>
        </div>
    );
};

export default Movie;
